import React, { useState, useEffect, useCallback } from 'react'
import CountUp from 'react-countup'
import { FormattedMessage } from 'react-intl'
import {
  enableBodyScroll,
  disableBodyScroll,
  clearAllBodyScrollLocks
} from 'body-scroll-lock'
import usePrev from 'src/hooks/usePrev'
import { ModalClose } from 'src/Components/Modal'
import {
  If,
  Unless,
  Button,
  Modal,
  Text,
  Rate,
  APR,
  DisclaimersButton,
  InlineBulletedList,
  Format
} from 'src/Components'
import { IconAngleLeft, IconAngleRight, IconPhone } from 'src/Components/Icon'
import RefiViz from 'src/Components/RefiViz'
import RefiMessages from 'src/Components/InterestSection/RefiByYearsInHome/RefiMessages'
import RefiRequest from 'src/Components/InterestSection/Refinance/RefiRequest'
import Questions from 'src/Components/Questions/QuestionsContainer'
import { REFI_CHART_MESSAGES } from 'src/constants/refinance'
import { QUESTIONS } from 'src/constants/messages'
import { ARM_MORTGAGE_TYPE, LOAN_TERM_MESSAGES } from 'src/constants/loan'
import styles from './RefiDetailsModal.module.scss'

import { min, max } from 'd3'

const RefiNavigator = ({
  type,
  years,
  longterm,
  rate,
  apr,
  navOption,
  yearsInHome
}) => {
  const prevNumber = usePrev(longterm)

  let color
  const bad = longterm <= 0
  const caution = type === ARM_MORTGAGE_TYPE && yearsInHome > years
  if (bad) {
    color = 'red'
  } else if (caution) {
    color = 'orange'
  } else {
    color = 'green'
  }

  return (
    <div className={styles.navigator}>
      <div className={styles.buttonBuffer}>
        <Button onClick={() => navOption(-1)} compact>
          <IconAngleLeft />
        </Button>
      </div>
      <div className={styles.navFade}>
        <div className={styles.deets}>
          <Text size='l'>
            <RefiMessages.LoanName type={type} years={years} />
          </Text>
        </div>
        <div className={styles.longterm}>
          <Text size='xxl' weight='light' color={color}>
            <CountUp
              start={prevNumber}
              end={longterm}
              duration={0.5}
              formattingFn={Format.currency}
            />
          </Text>
          <If condition={caution}>
            <Text
              className={styles.armCaution}
              size='s'
              color={color}
              weight='semibold'
            >
              <Unless condition={bad}>
                <FormattedMessage
                  id='Refinance.savings.armLock.caution'
                  defaultMessage='Saved Over {years} Years'
                  values={{ years }}
                />
              </Unless>
              <If condition={bad}>
                <FormattedMessage
                  id='Refinance.savings.armLock.bad'
                  defaultMessage='Lost Over {years} Years'
                  values={{ years }}
                />
              </If>
            </Text>
          </If>
        </div>
        <Text color='quiet' element='div' className={styles.deets}>
          <InlineBulletedList>
            <Rate value={rate} />
            <APR value={apr} />
          </InlineBulletedList>
        </Text>
      </div>
      <div className={styles.buttonBuffer}>
        <Button onClick={() => navOption(1)} compact>
          <IconAngleRight />
        </Button>
      </div>
    </div>
  )
}

const RefiQuestionsContent = ({
  contactFirstName,
  contactPhone,
  hasSponsor,
  type,
  years
}) => (
  <div className={styles.questionsContent}>
    <If condition={hasSponsor}>
      <RefiRequestButton type={type} years={years} />
    </If>
    <Button
      className={styles.callButton}
      iconStart={IconPhone}
      element='a'
      href={`tel:${contactPhone}`}
      track='RefiDetailsCall'
    >
      <FormattedMessage
        id='RefiDetailsModal.questions.call'
        defaultMessage='Call {name}'
        values={{
          name: contactFirstName
        }}
      />
    </Button>
  </div>
)

const RefiRequestButton = ({ type, years }) => (
  <If condition={type && years}>
    <RefiRequest
      withPrimaryButton
      className={styles.sendButton}
      messageProps={{
        ...QUESTIONS.refiDetailsCheck,
        values: {
          label: <RefiMessages.LoanName type={type} years={years} />
        }
      }}
      label={LOAN_TERM_MESSAGES[type.toLowerCase()].defaultMessage.replace(
        '{years}',
        years
      )}
    />
  </If>
)

const RefiDetailsModal = details => {
  const {
    intl,
    index,
    type,
    years,
    contactFirstName,
    setRefiIndex,
    open,
    data,
    hasSponsor
  } = details
  const buildOptions = useCallback(
    () =>
      data
        ? {
            verticalPaddingFactor: 0,
            min: min(data ? data.map(d => d.y) : 0),
            max: max(data ? data.map(d => d.y) : 0),
            datapoints: data.map(attrs => ({
              ...attrs,
              tooltipEmphasis:
                type === ARM_MORTGAGE_TYPE && attrs.x > years
                  ? intl.formatMessage(REFI_CHART_MESSAGES.unknown)
                  : Format.currency(attrs.y),
              tooltipDescription: intl.formatMessage(
                REFI_CHART_MESSAGES.tooltip,
                { year: attrs.x }
              )
            }))
          }
        : null,
    [data, type, intl, years]
  )
  const [chartOptions, setChartOptions] = useState(buildOptions())

  let scrollbox
  useEffect(() => {
    setChartOptions(buildOptions())

    if (scrollbox) {
      clearAllBodyScrollLocks()
      disableBodyScroll(scrollbox)

      if (typeof scrollbox.scroll === 'function') {
        scrollbox.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth'
        })
      } else {
        scrollbox.scrollTop = 0
        scrollbox.scrollLeft = 0
      }

      return () => {
        enableBodyScroll(scrollbox)
      }
    }
  }, [buildOptions, scrollbox, type, years])

  const updateIndex = dir => {
    let set = index
    if (dir > 0) {
      set++
    }
    if (dir < 0) {
      set--
    }

    setRefiIndex(set)
  }

  const close = () => {
    if (scrollbox) {
      enableBodyScroll(scrollbox)
    }
    setRefiIndex(null)
  }

  return (
    <Modal
      className={styles.modal}
      open={open || index !== null}
      toggle={close}
      overrideContent
    >
      <ModalClose className={styles.closeButton} toggle={close} />
      <RefiNavigator {...details} navOption={updateIndex} />
      <div ref={r => (scrollbox = r)} className={styles.scrollbox}>
        <div className={styles.detail}>
          <div className={styles.headline}>
            <Text size='xl' weight='light'>
              <RefiMessages.DetailsHeadline {...details} />
            </Text>
          </div>
          <Unless condition={hasSponsor}>
            <RefiRequestButton {...details} />
          </Unless>
          <div className={styles.specifics}>
            <div className={styles.vizualization}>
              <RefiViz {...details} options={chartOptions} />
            </div>
            <div className={styles.bullets}>
              <RefiMessages.DetailsBullets {...details} />
            </div>
          </div>
          <div className={styles.generic}>
            <Text size='l' weight='light' element='div'>
              <FormattedMessage
                id='Refinance.details.generic.title'
                defaultMessage='How much does refinancing cost?'
              />
            </Text>
            <If condition={hasSponsor}>
              <Text color='quiet' size='s'>
                <FormattedMessage
                  id='Refinance.details.generic.p2'
                  defaultMessage='Your specific mortgage estimate depends on market rates, your credit score, home occupancy status, and refi goals. You can sometimes roll the refi costs into your loan instead of bringing cash to the closing.'
                />
              </Text>
            </If>
            <Unless condition={hasSponsor}>
              <Text color='quiet' size='s'>
                <FormattedMessage
                  id='Refinance.details.generic.p1'
                  defaultMessage='{contactFirstName} is happy to walk you through their specific fee structure. Your specific mortgage estimate depends on market rates, your credit score, home occupancy status, and refi goals. You can sometimes roll the refi costs into your loan instead of bringing cash to the closing.'
                  values={{ contactFirstName }}
                />
              </Text>
            </Unless>
          </div>
        </div>
        <div className={styles.questions}>
          <Questions
            trackActivityForCospo
            location='RefiDetailsModal'
            questionTopic='Refinancing'
            questions={[
              { question: intl.formatMessage(QUESTIONS.refinanceRate) },
              { question: intl.formatMessage(QUESTIONS.refinanceFees) },
              { placeholder: intl.formatMessage(QUESTIONS.placeholder) }
            ]}
          >
            <RefiQuestionsContent {...details} />
            <If condition={hasSponsor}>
              <Text
                size='l'
                align='center'
                weight='light'
                className={styles.questionsHeading}
              >
                <FormattedMessage
                  id='Refinance.details.ask.with.sponsor'
                  defaultMessage='Ask {contactFirstName} a question...'
                  values={{ contactFirstName }}
                />
              </Text>
            </If>
            <If condition={!hasSponsor}>
              <Text
                size='l'
                align='center'
                weight='light'
                className={styles.questionsHeading}
              >
                <FormattedMessage
                  id='Refinance.details.ask'
                  defaultMessage='Ask a question...'
                />
              </Text>
            </If>
          </Questions>
        </div>
        <div className={styles.legal}>
          <DisclaimersButton />
        </div>
      </div>
    </Modal>
  )
}

export default RefiDetailsModal
